import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, X } from "lucide-react";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import PwaInstallGuideDialog from "@/components/pwa/PwaInstallGuideDialog";
import { getInstallGuideCopy } from "@/lib/pwaInstallI18n";
import { getPwaInstallInstructions, needsManualPwaInstall } from "@/lib/pwaInstall";

const DISMISS_KEY = "reshet_install_banner_dismissed";
const DISMISS_DAYS = 7;

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallBanner() {
  const { canInstall, hasNativePrompt, triggerInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(() => isDismissedRecently());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const manualInstall = needsManualPwaInstall();
  const isVisible = canInstall && !dismissed && (hasNativePrompt || manualInstall);
  const t = getInstallGuideCopy();

  const handleInstallClick = async () => {
    if (hasNativePrompt && !manualInstall) {
      setLoading(true);
      try {
        await triggerInstall();
      } finally {
        setLoading(false);
      }
      return;
    }
    setDialogOpen(true);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setDismissed(true);
  };

  if (!isVisible) return null;

  const instructions = getPwaInstallInstructions();

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 z-[100] animate-in fade-in slide-in-from-bottom-10 rounded-2xl border border-emerald-100 bg-white p-4 shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-emerald-100 p-2 text-emerald-600">
              <Smartphone className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{t.bannerTitle}</p>
              <p className="text-xs text-gray-500">
                {manualInstall ? t.title : t.bannerSubtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleInstallClick}
              size="sm"
              className="bg-emerald-600 font-bold text-white hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? "..." : manualInstall ? t.howButton : t.installButton}
            </Button>
            <button type="button" onClick={handleDismiss} className="p-1 text-gray-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <PwaInstallGuideDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        instructions={instructions}
      />
    </>
  );
}
